import React, { useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Briefcase,
  Calendar,
  MessageSquare,
  Users,
  UserPlus,
  Folder,
  DollarSign,
  BarChart,
  Settings,
  LogOut,
  Menu,
  X,
  Landmark,
  Sparkles,
  PenTool,
  Megaphone,
  ShieldAlert,
  Star,
  Timer,
  PieChart,
  Clock,
} from "lucide-react";
import { useUserDataContext } from "../context/UserDataContext";
import NotificationDropdown from "./notifications/NotificationDropdown";
import WhiteLogo from "../assets/KavoaWhiteLogo.png";
import { GetBaseURL } from "./utils/urlUtils";

interface NavItem {
  id: string;
  label: string;
  icon: React.FC<any>;
  path: string;
  requiredPlan?: "pro" | "elite";
}

const navigationItems: NavItem[] = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, path: "/" },
  { id: "matters", label: "Matters", icon: Briefcase, path: "/matters" },
  { id: "clients", label: "Clients", icon: Users, path: "/clients" },
  { id: "leads", label: "Leads", icon: UserPlus, path: "/leads", requiredPlan: "pro" },
  { id: "calendar", label: "Calendar", icon: Calendar, path: "/calendar" },
  { id: "communications", label: "Communications", icon: MessageSquare, path: "/communications", requiredPlan: "pro" },
  { id: "documents", label: "Documents", icon: Folder, path: "/documents" },
  { id: "esignatures", label: "E-Signatures", icon: PenTool, path: "/esign", requiredPlan: "pro" },
  { id: "time-tracking", label: "Time Tracking", icon: Clock, path: "/time-tracking" },
  { id: "billing", label: "Billing", icon: DollarSign, path: "/billing" },
  { id: "accounting", label: "Accounting", icon: Landmark, path: "/accounting", requiredPlan: "elite" },
  { id: "reports", label: "Reports", icon: BarChart, path: "/reports" },
  { id: "conflict-checks", label: "Conflict Checks", icon: ShieldAlert, path: "/conflict-checks" },
  { id: "reviews", label: "Reviews", icon: Star, path: "/reviews" },
  { id: "marketing", label: "Marketing", icon: Megaphone, path: "/broadcasts", requiredPlan: "pro" },
  { id: "settings", label: "Settings", icon: Settings, path: "/settings" },
];

const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "U";
  return parts
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");
};

interface SidebarProps {
  handleSignOut: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ handleSignOut }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userData, firmData, canManageBilling } = useUserDataContext();
  const [mobileOpen, setMobileOpen] = useState(false);

  const userName = useMemo(() => {
    return userData?.name || firmData?.name || "User";
  }, [userData, firmData]);

  const userRole = useMemo(() => {
    return (userData as any)?.profile?.title || "Team Member";
  }, [userData]);

  const initials = useMemo(() => getInitials(userName), [userName]);

  // Custom logo only for free trial or elite tier
  const canCustomize = firmData?.isOnTrial || firmData?.subscriptionStatus === "elite";
  const logoSrc = canCustomize && firmData?.branding?.logoUrl && firmData?._id
    ? `${GetBaseURL()}/api/firm/logo/${firmData._id}`
    : WhiteLogo;

  const TIER_LEVEL: Record<string, number> = { "free trial": 0, basic: 1, pro: 2, elite: 3 };

  const canAccess = (requiredPlan?: "pro" | "elite"): boolean => {
    if (!requiredPlan) return true;
    const currentPlan = firmData?.subscriptionStatus || "free trial";
    const isOnTrial = firmData?.isOnTrial;
    if (isOnTrial) return true;
    return (TIER_LEVEL[currentPlan] ?? 0) >= (TIER_LEVEL[requiredPlan] ?? 0);
  };

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const handleNav = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleSignOutClick = () => {
    handleSignOut();
    navigate("/");
  };

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Logo/Header */}
      <div
        className="flex items-center justify-center px-6 py-5"
        style={{ borderBottom: "1px solid var(--color-primary-hover)" }}
      >
        <img
          src={logoSrc}
          alt={firmData?.name || "Kavoa"}
          className="h-20 rounded-lg object-contain"
          style={{ maxWidth: "180px" }}
        />
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-6 space-y-1" aria-label="Main navigation">
        {/* Conditionally inject IP nav items after Matters */}
        {(() => {
          const hasIp = firmData?.practiceAreas?.some(
            (pa: string) => pa === "intellectual_property" || pa === "ip"
          );
          const ipItems: NavItem[] = hasIp
            ? [
                { id: "ip-docket", label: "IP Docket", icon: Timer, path: "/ip/docket" },
                { id: "ip-portfolio", label: "IP Portfolio", icon: PieChart, path: "/ip/portfolio" },
              ]
            : [];
          const allItems: NavItem[] = [];
          navigationItems.forEach((item) => {
            allItems.push(item);
            if (item.id === "matters" && ipItems.length > 0) {
              allItems.push(...ipItems);
            }
          });
          return allItems;
        })().map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          const locked = !canAccess(item.requiredPlan);
          return (
            <button
              key={item.id}
              type="button"
              data-tour={`nav-${item.id}`}
              onClick={() => handleNav(item.path)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                active ? "text-black" : "text-white/90 hover:text-white"
              } ${locked ? "opacity-60" : ""}`}
              style={active ? { backgroundColor: "#f8fffc" } : {}}
              onMouseEnter={(e) => {
                if (!active) e.currentTarget.style.backgroundColor = "var(--color-primary-hover)";
              }}
              onMouseLeave={(e) => {
                if (!active) e.currentTarget.style.backgroundColor = "transparent";
              }}
              aria-current={active ? "page" : undefined}
            >
              <Icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
              <span>{item.label}</span>
              {locked && item.requiredPlan === "pro" && (
                <span className="ml-auto inline-flex items-center gap-1 bg-brand-accent text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                  PRO
                </span>
              )}
              {locked && item.requiredPlan === "elite" && (
                <span className="ml-auto inline-flex items-center gap-1 bg-purple-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                  FIRM
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Trial Upgrade CTA - shown only during trial (and only after firm data loads) */}
      {firmData?._id && firmData?.isOnTrial && (
        <div className="px-3 pb-2">
          <div
            className={`rounded-xl p-4 transition-all ${canManageBilling ? "cursor-pointer hover:brightness-110" : ""}`}
            style={{ background: "linear-gradient(135deg, var(--color-accent) 0%, var(--color-primary-hover) 100%)" }}
            onClick={canManageBilling ? () => navigate("/settings/subscription") : undefined}
          >
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-4 w-4 text-white/60" />
              <span className="text-xs font-bold text-white uppercase tracking-wider">Free Trial</span>
            </div>
            <p className="text-sm font-semibold text-white mb-1">
              {(() => {
                const exp = firmData?.stripe?.trialExpiration;
                if (!exp) return "Trial active";
                const days = Math.max(0, Math.ceil((new Date(exp).getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
                return `${days} day${days !== 1 ? "s" : ""} remaining`;
              })()}
            </p>
            <p className="text-xs text-white/60 mb-3">
              {canManageBilling ? "Upgrade to keep your data" : "Contact your admin to upgrade"}
            </p>
            {canManageBilling && (
              <div className="bg-white/20 hover:bg-white/30 rounded-lg px-3 py-2 text-center text-sm font-bold text-white transition-colors">
                View Plans
              </div>
            )}
          </div>
        </div>
      )}

      {/* User Profile */}
      <div className="px-3 py-4" style={{ borderTop: "1px solid var(--color-primary-hover)" }}>
        <div className="flex items-center gap-3 px-3 py-2 rounded-lg">
          <div
            className="h-9 w-9 rounded-full flex items-center justify-center text-white font-semibold text-sm"
            style={{ backgroundColor: "var(--color-accent)" }}
          >
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{userName}</p>
            <p className="text-xs text-white/60 truncate">{userRole}</p>
          </div>
          <NotificationDropdown />
          <button
            type="button"
            onClick={handleSignOutClick}
            className="p-2 rounded-lg text-white/60 hover:text-white transition-colors"
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "var(--color-primary-hover)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
            }}
            aria-label="Sign out"
            title="Sign out"
          >
            <LogOut className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile hamburger */}
      <button
        type="button"
        className="md:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-white shadow-lg border border-slate-200"
        onClick={() => setMobileOpen(!mobileOpen)}
        aria-label="Toggle navigation"
        data-tour="hamburger-menu"
      >
        {mobileOpen ? (
          <X className="h-6 w-6 text-slate-700" />
        ) : (
          <Menu className="h-6 w-6 text-slate-700" />
        )}
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile sidebar (drawer) */}
      <aside
        className={`md:hidden fixed inset-y-0 left-0 z-40 w-64 transform transition-transform duration-300 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        style={{ backgroundColor: "var(--color-primary)", borderRight: "1px solid var(--color-primary-hover)" }}
      >
        {sidebarContent}
      </aside>

      {/* Desktop sidebar */}
      <aside
        className="hidden md:block w-64 flex-shrink-0 sticky top-0 h-screen overflow-y-auto"
        style={{ backgroundColor: "var(--color-primary)", borderRight: "1px solid var(--color-primary-hover)" }}
      >
        {sidebarContent}
      </aside>
    </>
  );
};

export default Sidebar;
